import axios from 'axios';
import Swal from 'sweetalert2';

/*
 * ==========================================================================
 * Acessibilidade dos avisos (WCAG 2.2)
 *
 * O que estava errado, e por que cada item é um critério e não gosto:
 *
 * 1. 2.2.1 Limite de tempo ajustável (nível A) — os avisos sumiam em 3 a 10
 *    segundos e o único jeito de segurar era passar o MOUSE em cima
 *    (`mouseenter`/`mouseleave`). Quem usa teclado, leitor de tela ou toque não
 *    tinha nenhuma forma de pausar nem de prolongar. Pior: quem usa leitor de tela
 *    recebe o anúncio depois do que já estava sendo lido, e um aviso de 3 s
 *    frequentemente já não está mais na tela quando a pessoa vai procurá-lo.
 *
 *    Três medidas, e as três são exigidas em conjunto:
 *      - PAUSAR TAMBÉM POR FOCO (`focusin`/`focusout`): equivalente de teclado do
 *        `mouseenter`, que é o que o critério chama de "prolongar";
 *      - BOTÃO DE FECHAR (`showCloseButton`): é o "desligar" — o usuário encerra o
 *        aviso quando quiser, sem depender do relógio;
 *      - TEMPOS MAIORES. `TEMPO_LEITURA_*` abaixo partem de ~180 palavras por
 *        minuto, com piso generoso. 3 s não dá para ler uma mensagem de erro de
 *        servidor, muito menos para achá-la na tela primeiro.
 *
 * 2. 2.2.1 de novo, no caso mais grave: `alertaBootstrapRelatorio` mostra um botão
 *    "Baixar" e fechava em 10 s. O relógio não parava com o foco, então quem navega
 *    por Tab tinha 10 segundos para atravessar a página até o botão — passando
 *    disso, a ÚNICA forma de baixar aquele relatório pela tela desaparecia. Aviso
 *    com ação agora NÃO tem temporizador (ver `SEM_TEMPORIZADOR`).
 *
 * 3. 1.4.10 Refluxo — a largura vinha em porcentagem da tela (`20%`, `40%`). Num
 *    celular de 360 px, 20% são 72 px de caixa: a mensagem virava uma coluna de uma
 *    ou duas letras por linha. Agora é `min(92vw, …px)`: limite em pixels no
 *    desktop, quase a largura inteira no celular.
 *
 * 4. 2.2.4 Interrupções (AAA) — continua PENDENTE de decisão de produto: não existe
 *    preferência do usuário para adiar/suprimir avisos. Registrado em
 *    docs/acessibilidade.
 * ==========================================================================
 */

/** Aviso que exige ação do usuário não pode ter relógio (WCAG 2.2.1). */
const SEM_TEMPORIZADOR = undefined;

/** Tempo de leitura confortável para aviso de sucesso/informação, em ms. */
const TEMPO_LEITURA_CURTO = 7000;

/**
 * Tempo de leitura para ERRO, em ms. Mensagem de erro é a que mais precisa ser lida
 * inteira (e às vezes copiada), e é a que mais costuma ser longa — vem do servidor.
 */
const TEMPO_LEITURA_ERRO = 15000;

/**
 * Largura responsiva do aviso.
 *
 * O `min()` é o que resolve os dois extremos com um valor só: no desktop vale o teto
 * em pixels; no celular vale `92vw`, deixando folga para o dedo nas laterais.
 */
const larguraResponsiva = (tetoPx: number) => `min(92vw, ${tetoPx}px)`;

/**
 * Pausa o relógio do aviso enquanto ele estiver sob o ponteiro OU com o foco dentro.
 *
 * O `focusin`/`focusout` é o equivalente de teclado do `mouseenter`/`mouseleave`, e é
 * ele que faz o critério 2.2.1 ("prolongar") valer para quem não usa mouse — antes só
 * existia o par de mouse.
 */
const pausarEnquantoEmUso = (aviso: HTMLElement) => {
    aviso.addEventListener('mouseenter', Swal.stopTimer);
    aviso.addEventListener('mouseleave', Swal.resumeTimer);
    aviso.addEventListener('focusin', Swal.stopTimer);
    aviso.addEventListener('focusout', Swal.resumeTimer);
};

/**
 * Teto de caracteres da mensagem exibida.
 *
 * Erro cru de servidor (um `SqlException` com a consulta inteira, um stack trace) chega com
 * milhares de caracteres, e o balão do aviso cresce com o conteúdo: já saiu uma faixa ocupando a
 * altura inteira da tela, empurrando o botão de fechar para fora. O texto continua sendo
 * ANUNCIADO por inteiro até este limite — o que se corta é a cauda, que nesses casos é pilha de
 * chamada e não informação para quem usa o sistema.
 */
const MAXIMO_DE_CARACTERES = 1200;

/** Altura máxima do balão, em porcentagem da tela, antes de o texto passar a rolar. */
const ALTURA_MAXIMA = '40vh';

/**
 * O que realmente vai para o balão: nunca vazio, nunca sem fim.
 *
 * <p><b>Nunca vazio</b> porque `title: ''` no SweetAlert2 desenha o balão só com o ícone — um
 * aviso em branco, que é pior do que nenhum: o usuário vê que ALGO aconteceu e não tem como saber
 * o quê. Acontecia sempre que a leitura do corpo do erro falhava (ver `itensDeErro` em
 * `erro-http.util.ts`).</p>
 */
const textoExibivel = (msg: string, padrao: string): string => {
    const texto = (msg ?? '').toString().trim();

    if (!texto) return padrao;

    if (texto.length <= MAXIMO_DE_CARACTERES) return texto;

    return texto.slice(0, MAXIMO_DE_CARACTERES).trimEnd() + '… (mensagem encurtada)';
};

/**
 * Prende a altura do balão e deixa o texto rolar dentro dele.
 *
 * <p>Aplicado no `didOpen` e não por CSS global de propósito: a folha de estilo do SweetAlert2 é
 * compartilhada com os modais do sistema (ver `dialogo-acessivel.ts`), e um seletor global
 * mexeria também nos diálogos de confirmação. A área rolável recebe `tabindex="0"` porque
 * conteúdo que rola precisa ser alcançável pelo teclado (WCAG 2.1.1) — sem isso, quem não usa
 * mouse não chega ao fim de uma mensagem longa.</p>
 */
const limitarAltura = (aviso: HTMLElement) => {
    pausarEnquantoEmUso(aviso);

    const corpo = aviso.querySelector<HTMLElement>('.swal2-title, .swal2-html-container');

    if (!corpo) return;

    corpo.style.maxHeight = ALTURA_MAXIMA;
    corpo.style.overflowY = 'auto';
    corpo.style.overflowWrap = 'anywhere';

    if (corpo.scrollHeight > corpo.clientHeight) {
        corpo.setAttribute('tabindex', '0');
        corpo.setAttribute('role', 'region');
        corpo.setAttribute('aria-label', 'Mensagem completa, use as setas para rolar');
    }
};

class ToastService {

    // SweetAlert2 só mantém um toast na tela por vez: um novo Swal.fire() sempre substitui o que
    // estiver aberto, mesmo que este tenha um botão de ação pendente (ex: o aviso de encerramento
    // de sessão por inatividade, com "Renovar Minha Sessão"). Usado para não deixar um toast
    // meramente informativo (ex: "sessão renovada automaticamente") apagar um aviso acionável que
    // já esteja na tela.
    existeAlertaAtivo(): boolean {
        return Swal.isVisible();
    }

    alertaAviso(
        msg: string,
        timer: number,
        showConfirmButton: boolean = false,
        confirmButtonText: string = 'OK',
        width: string = larguraResponsiva(420),
        useHtml: boolean = false
    ) {
        Swal.fire({
            toast: true,
            position: 'bottom-end',
            showConfirmButton,
            confirmButtonText,
            // Fechar por vontade própria é o "desligar" exigido por 2.2.1.
            showCloseButton: true,
            closeButtonAriaLabel: 'Fechar aviso',
            customClass: {
                confirmButton: 'btn btn-info',
            },
            /*
             * Aviso COM BOTÃO DE AÇÃO não recebe relógio: o botão é a única forma de
             * executar aquela ação, e um relógio que não para no foco tira do usuário de
             * teclado a possibilidade de chegar até ele (WCAG 2.2.1).
             *
             * O chamador continua passando `timer`; ele só é respeitado quando o aviso é
             * meramente informativo. O aviso de sessão a expirar (App.vue) passa por aqui
             * COM botão — e é justamente o que não pode sumir sozinho.
             */
            timer: showConfirmButton ? SEM_TEMPORIZADOR : timer,
            width,
            timerProgressBar: !showConfirmButton,
            didOpen: limitarAltura,

            ...(useHtml
                ? { html: msg }
                : { title: textoExibivel(msg, 'Não foi possível concluir a operação.') }),

            icon: 'info'
        });
    }

    /*
     * O padrão era 3000 ms. Três segundos é menos do que o tempo entre o leitor de tela
     * anunciar a região viva e o usuário conseguir navegar até ela para ler — a
     * confirmação de que o registro foi salvo simplesmente não existia para quem não
     * enxerga a tela.
     */
    alertaSucesso(msg: string, useHtml: boolean = false, timer: number = TEMPO_LEITURA_CURTO) {
        Swal.fire({
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            showCloseButton: true,
            closeButtonAriaLabel: 'Fechar aviso',
            width: larguraResponsiva(420),
            timer,
            timerProgressBar: true,
            didOpen: limitarAltura,
            ...(useHtml ? { html: msg } : { title: textoExibivel(msg, 'Operação concluída.') }),
            icon: 'success'
        });
    }

    fechar() {
        Swal.close();
    }

    /*
     * Erro é a mensagem mais importante da tela e era a que sumia mais rápido em relação
     * ao tamanho: 5 s para um texto de erro de servidor, que costuma ter duas ou três
     * linhas. Além do tempo maior, ganha botão de fechar e pausa por foco — o usuário
     * decide quando encerrar (WCAG 2.2.1) em vez de correr contra o relógio.
     */
    alertaErro(msg: string, useHtml: boolean = false) {
        Swal.fire({
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            showCloseButton: true,
            closeButtonAriaLabel: 'Fechar mensagem de erro',
            width: larguraResponsiva(480),
            timer: TEMPO_LEITURA_ERRO,
            timerProgressBar: true,
            didOpen: limitarAltura,
            ...(useHtml
                ? { html: msg }
                : {
                    title: textoExibivel(
                        msg,
                        'Ocorreu um erro e o servidor não informou o motivo. Tente novamente; se continuar, avise o suporte.'),
                }),
            icon: 'error',
        });
    }

    alertaBootstrapRelatorio(nomeArquivo: string, url: string) {
        const urlRelatorio = url;
        const nomeArquivoRelatorio = nomeArquivo;

        let type = {};

        if (url.toLowerCase().includes('pdf')) {
            type = { type: 'application/pdf' };
        } else if (url.toLowerCase().includes('xls')) {
            type = { type: 'application/vnd.ms-excel' };
        }

        Swal.fire({
            toast: true,
            position: 'bottom-end',
            confirmButtonText: 'Baixar',
            showConfirmButton: true,
            /*
             * SEM relógio, e este é o caso mais grave de 2.2.1 do serviço: o botão
             * "Baixar" era a única forma de obter o relatório pela tela e desaparecia em
             * 10 s, sem parar no foco. Quem navega por Tab a partir do início da página
             * não chegava até ele em tempo — o recurso simplesmente não existia sem mouse.
             *
             * Como agora não fecha sozinho, o botão de fechar é obrigatório.
             */
            timer: SEM_TEMPORIZADOR,
            showCloseButton: true,
            closeButtonAriaLabel: 'Fechar aviso do relatório',
            width: larguraResponsiva(560),
            timerProgressBar: false,
            didOpen: pausarEnquantoEmUso,

            html: `<p>${nomeArquivoRelatorio.length > 12
                ? nomeArquivoRelatorio.substring(0, 12) + '...'
                : nomeArquivoRelatorio
            } gerado com sucesso, clique no botão para visualizar ou acesse as notificações para visualizá-lo.</p>`,

            icon: 'info',

            customClass: {
                popup: 'd-flex justify-content-between',
                actions: 'd-flex justify-content-end',
            },

            preConfirm: async () => {
                axios.get(urlRelatorio, { responseType: 'blob' })
                    .then((response: any) => {
                        const url = window.URL.createObjectURL(
                            new Blob([response.data], type)
                        );

                        const link = document.createElement('a');

                        link.href = url;
                        link.setAttribute('download', nomeArquivoRelatorio);

                        document.body.appendChild(link);

                        link.click();

                        document.body.removeChild(link);

                        URL.revokeObjectURL(url);
                    });
            }
        });
    }

    confirmDelete(itemLabel: string) {
        return Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-danger btn-simple'
            },
            buttonsStyling: false
        })
            .fire({
                title: 'Tem certeza?',
                text: `Deseja deletar o item '${itemLabel}'?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Confirmar',
                cancelButtonText: 'Cancelar'
            });
    }

    // Modal centralizado de confirmação genérico (aceita HTML na mensagem).
    // Devolve a promise do SweetAlert2 — o chamador checa result.isConfirmed.
    confirmar(
        titulo: string,
        mensagemHtml: string,
        confirmButtonText: string = 'Confirmar',
        cancelButtonText: string = 'Cancelar'
    ) {
        return Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-secondary btn-simple'
            },
            buttonsStyling: false
        })
            .fire({
                title: titulo,
                html: mensagemHtml,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText
            });
    }

    // Modal centralizado que pede um texto.
    // Devolve a promise do SweetAlert2 — o chamador checa result.isConfirmed
    // e lê o texto digitado em result.value.
    solicitarTexto(
        titulo: string,
        mensagemHtml: string,
        rotulo: string,
        maxLength: number = 0,
        confirmButtonText: string = 'Confirmar',
        cancelButtonText: string = 'Cancelar'
    ) {
        return Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-secondary btn-simple'
            },
            buttonsStyling: false
        })
            .fire({
                title: titulo,
                html: mensagemHtml,
                icon: 'question',
                input: 'text',
                inputLabel: rotulo,
                inputAttributes: maxLength > 0
                    ? { maxlength: String(maxLength) }
                    : undefined,
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText,
                inputValidator: (valor: string) =>
                    valor && valor.trim()
                        ? null
                        : 'Informe o texto.'
            });
    }

    // Confirmação que também pede uma data (input date do navegador, formato YYYY-MM-DD).
    // Devolve a promise do SweetAlert2 — o chamador checa result.isConfirmed
    // e lê a data em result.value.
    confirmarComData(
        titulo: string,
        mensagemHtml: string,
        dataInicial: string,
        rotuloData: string,
        confirmButtonText: string = 'Confirmar',
        cancelButtonText: string = 'Cancelar'
    ) {
        return Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-secondary btn-simple'
            },
            buttonsStyling: false
        })
            .fire({
                title: titulo,
                html: mensagemHtml,
                icon: 'warning',
                input: 'date',
                inputLabel: rotuloData,
                inputValue: dataInicial,
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText,
                inputValidator: (valor: string) =>
                    valor
                        ? null
                        : 'Informe a data.'
            });
    }

    // Modal centralizado apenas informativo (botão OK). Aceita HTML na mensagem.
    informar(
        titulo: string,
        mensagemHtml: string,
        icone: 'info' | 'warning' | 'error' | 'success' = 'info'
    ) {
        return Swal.fire({
            title: titulo,
            html: mensagemHtml,
            icon: icone,
            confirmButtonText: 'OK',
            customClass: {
                confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false
        });
    }

}

export default new ToastService();